import { Modal } from '../../../../componentsV2/Modal';
import {
  TopAccountRecord,
  TopAccountsCondensedTable,
} from './components/TopAccountsCondensedTable';
import { useQuery } from '@tanstack/react-query';
import { coreService } from '../../../../services/core/core-service';
import { PropsWithChildren, useMemo, useState } from 'react';
import Card from '../../../../componentsV2/Card';

import Styles from './styles';
import { Button, Collapse, Flex, Pagination, Select, Space, Spin, Tooltip, Typography } from 'antd';
import { DashboardFilterProvider } from '../../../shared/dashboard-filters/dashboard-filters.context';
import { useDashboardFilters } from '../../../shared/dashboard-filters/dashboard-filters.hooks';
import { addDays } from 'date-fns';
import { useToast } from '../../../../hooks/useToast';
import {
  CaretRightOutlined,
  CopyOutlined,
  DatabaseOutlined,
  EditOutlined,
  ExperimentOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  PushpinOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { Alert } from '../../../../components/alerts/Alert';
import { useSelector } from 'react-redux';
import { Store } from '../../../../redux/typings/store';
import { InsightsFilters, TopicType } from '../../../../services/types';
import { useAnalytics } from '../../../analytics/hooks/useAnalytics';

const { Container, CardContents, SummaryContainer } = Styles;

interface Props {
  open: boolean;
  topicId: string;
  filters: {
    from: string;
    to: string;
    accountId?: string;
  };
  onClose: () => void;
  onEditTopicClicked?: (args: { id: string; name: string; description: string }) => void;
  onFollowTopicClicked?: (id: string) => void;
  onUnfollowTopicClicked?: (id: string) => void;
}

export const Wrapper: React.FC<PropsWithChildren<Omit<Props, 'filters'>>> = ({
  topicId,
  open,
  onClose,
  onEditTopicClicked,
  onFollowTopicClicked,
  onUnfollowTopicClicked,
  children,
}) => {
  const { sendEvent } = useAnalytics();
  const { filters, getAccountFilterOptions, getDateFilterOptions, render, changeAccountValue } =
    useDashboardFilters();

  const [topic, setTopic] = useState<TopicType | null>(null);

  const user = useSelector((store: Store) => store.session.user);
  const isOwner = user?.membership.role === 'OWNER' || user?.membership.role === 'ADMIN';

  const topAccountsFilters = useMemo(() => {
    const dateValues = getDateFilterOptions('dateRange');
    const accountValue = getAccountFilterOptions('account');

    return {
      from: dateValues[0] ? dateValues[0].toISOString() : addDays(new Date(), -30).toISOString(),
      to: dateValues[1] ? dateValues[1].toISOString() : new Date().toISOString(),
      accountId: accountValue || '',
    };
  }, [filters]);

  const [sortBy, setSortBy] = useState('mentions');
  const [page, setPage] = useState(1);
  const [summarizing, setIsSummarizing] = useState(false);
  const [summary, setSummary] = useState('');
  const [summarized, setSummarized] = useState(false);
  const { success } = useToast();

  const { isLoading: isLoadingTopic } = useQuery({
    queryKey: ['topic-details', topicId],
    queryFn: async () => {
      if (!topicId) return null;

      const topic = await coreService.getTopicById(topicId);
      return topic;
    },
    onSuccess: data => {
      setTopic(data);
    },
  });

  const handleClose = () => {
    changeAccountValue('account', []);
    onClose();
    setIsSummarizing(false);
    setSummary('');
    setSummarized(false);
  };

  const { data: topAccounts, isFetching: topAccountsFetching } = useQuery({
    queryKey: [
      'topic-modal-accounts',
      topAccountsFilters.from,
      topAccountsFilters.to,
      topAccountsFilters.accountId,
      page,
      topicId,
      sortBy,
      open,
    ],
    queryFn: async () => {
      if (!open) {
        return null;
      }

      if (!topicId) return null;

      const options: Partial<InsightsFilters> = {};
      if (topAccountsFilters.accountId) {
        options['accountIds'] = Array.isArray(topAccountsFilters.accountId)
          ? topAccountsFilters.accountId
          : [topAccountsFilters.accountId];
      }

      const topAcc = await coreService.getTopAccountsByTopicId(
        topicId,
        topAccountsFilters.from,
        topAccountsFilters.to,
        page,
        10,
        sortBy,
        options,
      );
      return topAcc;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const tableData: TopAccountRecord[] = useMemo(() => {
    if (!topAccounts) return [];

    return topAccounts.accounts.map(acc => {
      return {
        account: {
          id: acc.id,
          name: acc.name || '',
          provider: acc.provider || '',
        },
        mentions: acc.mentions || 0,
        meetings: acc.meetings || 0,
        arr: acc.arr || 0,
        renewalDate: acc.renewalDate ? new Date(acc.renewalDate) : new Date(),
      };
    });
  }, [topAccounts]);

  const accountFilter = useMemo(() => {
    return render('account');
  }, [topAccountsFilters]);

  const summarizeTopics = async () => {
    setIsSummarizing(true);
    setSummarized(false);
    coreService
      .summarizeTopics(topicId, topAccountsFilters.from, topAccountsFilters.to)
      .then(summarization => {
        setSummary(summarization);
        sendEvent('topic summarized');
      })
      .finally(() => {
        setSummarized(true);
        setIsSummarizing(false);
      });
  };

  const handleChangeSort = (sortBy: string) => {
    setSortBy(sortBy);
    setPage(1);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(summary);
    success('Summary copied to clipboard');
  };

  const handlePinTopic = async () => {
    const { pinned } = await coreService.pinTopic(topicId);
    setTopic(
      prev =>
        ({
          ...prev,
          pinned,
        } as TopicType),
    );

    success(
      pinned
        ? 'Topic pinned. This topic will remain visible even when other topics are automatically recalculated.'
        : 'Topic unpinned successfully. Unpinned topics may be recalculated automatically.',
    );
  };

  const handleDisableTopic = async () => {
    const { disabled } = await coreService.disableTopic(topicId);
    setTopic(
      prev =>
        ({
          ...prev,
          disabled,
        } as TopicType),
    );

    success(
      disabled
        ? 'Topic successfully disabled. Insights will no longer be added.'
        : 'Topic successfully enabled. Insights will be added.',
    );
  };

  if (isLoadingTopic || !topic) {
    return (
      <Modal
        hideFooter
        width={1296}
        open={open}
        title={'Loading topic...'}
        onClose={handleClose}
        zIndex={999}
      >
        <div style={{ textAlign: 'center' }}>
          <Spin />
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      hideFooter
      width={1296}
      open={open}
      title={
        <Flex gap={8} align="center">
          {topic.pinned && (
            <Tooltip title="This topic is pinned.">
              <PushpinOutlined style={{ width: '16px' }} />
            </Tooltip>
          )}

          {topic.disabled && (
            <Tooltip title="This topic is disabled.">
              <StopOutlined style={{ width: '16px' }} />
            </Tooltip>
          )}

          {topic.custom && (
            <Tooltip title="This is a custom topic.">
              <ExperimentOutlined style={{ width: '16px' }} />
            </Tooltip>
          )}

          <span>{topic.name}</span>
        </Flex>
      }
      onClose={handleClose}
      zIndex={999}
      headerExtraContent={
        <Flex gap={8} align="center">
          <Tooltip title={!isOwner ? 'Only owners or admins can edit topics.' : null}>
            <Button
              disabled={!isOwner}
              onClick={() => {
                if (onEditTopicClicked) {
                  onEditTopicClicked({
                    id: topic.id,
                    name: topic.name,
                    description: topic.description,
                  });
                }
              }}
            >
              <Space>
                <EditOutlined />
                Edit
              </Space>
            </Button>
          </Tooltip>

          <Tooltip
            title={
              !isOwner
                ? 'Only owners or admins can pin topics.'
                : topic.custom
                ? 'Custom made topics cannot be pinned. They will not be recalculated.'
                : null
            }
          >
            <Button disabled={!isOwner || topic.custom} onClick={handlePinTopic}>
              <Space>
                <PushpinOutlined />
                {topic.custom ? 'Pin' : topic.pinned ? 'Unpin' : 'Pin'}
              </Space>
            </Button>
          </Tooltip>

          <Tooltip
            title={
              !isOwner
                ? 'Only owners or admins can disable topics.'
                : topic.pinned
                ? 'Cannot disable a pinned topic.'
                : null
            }
          >
            <Button disabled={!isOwner || topic.pinned} onClick={handleDisableTopic}>
              <Space>
                {topic.disabled ? <CaretRightOutlined /> : <StopOutlined />}
                {topic.disabled ? 'Enable' : 'Disable'}
              </Space>
            </Button>
          </Tooltip>

          <Button
            onClick={() => {
              if (topic.following && onUnfollowTopicClicked) {
                onUnfollowTopicClicked(topic.id);
              }

              if (!topic.following && onFollowTopicClicked) {
                onFollowTopicClicked(topic.id);
              }
            }}
          >
            <Space>
              {topic.following ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              {topic.following ? 'Unfollow' : 'Follow'}
            </Space>
          </Button>
        </Flex>
      }
    >
      <Container>
        {children}
        <Flex vertical gap={8}>
          {topic.pinned && isOwner && (
            <Alert
              customIcon={<DatabaseOutlined style={{ color: '#21A6FF' }} />}
              message={`When a topic is pinned, it will not be removed when new topics are added. ${
                isOwner ? 'You' : 'Only owners or admins'
              } can unpin it anytime.`}
              variant="info"
              title=""
            />
          )}

          {topic.disabled && isOwner && (
            <Alert
              customIcon={<DatabaseOutlined style={{ color: '#21A6FF' }} />}
              message={`When a topic is disabled, it will no longer receive new insights. ${
                isOwner ? 'You' : 'Only owners or admins'
              } can re-enable it anytime.`}
              variant="warning"
              title=""
            />
          )}
        </Flex>
        <Flex gap={'8px'} justify={'space-between'}>
          <Flex gap={'8px'}>
            <Select
              value={sortBy}
              onChange={value => handleChangeSort(value)}
              options={[
                { value: 'mentions', label: '# of Detections' },
                { value: 'meetings', label: '# of Meetings' },
              ]}
            />
            {render('dateRange')}
            {accountFilter}
          </Flex>

          <Alert
            customIcon={<DatabaseOutlined style={{ color: '#21A6FF' }} />}
            message={
              !topAccounts?.eventsCount
                ? 'Loading information.'
                : `Insights come from ${topAccounts?.eventsCount} meeting recaps based on your applied filters and meeting access.`
            }
            variant="info"
            title=""
          />
        </Flex>
        <SummaryContainer>
          {summarized && (
            <Flex vertical gap={'8px'}>
              {' '}
              <Flex gap={'8px'}>
                <Button type="default" onClick={summarizeTopics}>
                  Summarize this topic
                </Button>
                <Button icon={<CopyOutlined />} onClick={handleCopy} />
              </Flex>
              {summary}
            </Flex>
          )}

          {!summarized && !summarizing && (
            <Button type="primary" onClick={summarizeTopics}>
              Summarize this topic
            </Button>
          )}

          {summarizing && (
            <Spin
              size="small"
              tip="Generating your summary. Closing the modal at any time will reset the summary."
              style={{
                display: 'inline-flex',
                minWidth: '600px',
                height: '100%',
                position: 'relative',
                padding: '20px',
              }}
            >
              <div />
            </Spin>
          )}
        </SummaryContainer>

        <Collapse
          defaultActiveKey={[]}
          items={[
            {
              key: '1',
              label: 'Description',
              children: (
                <Typography.Text>
                  {topic.description ?? 'Description not currently available.'}
                </Typography.Text>
              ),
            },
          ]}
        />

        <Card title="Accounts">
          <CardContents>
            <TopAccountsCondensedTable
              elems={tableData}
              loading={topAccountsFetching}
              filters={topAccountsFilters}
              sortBy={sortBy}
              topic={topic}
            />
            <Pagination
              style={{
                alignSelf: 'flex-end',
              }}
              onChange={value => setPage(value)}
              hideOnSinglePage
              showSizeChanger={false}
              total={topAccounts?.pagination?.items?.total || 1}
              pageSize={topAccounts?.pagination?.pages?.pageSize || 10}
              current={topAccounts?.pagination?.pages?.currentPage || 1}
            />
          </CardContents>
        </Card>
      </Container>
    </Modal>
  );
};

export const TopicDetailModal: React.FC<PropsWithChildren<Props>> = ({
  topicId,
  open,
  onClose,
  onEditTopicClicked,
  onFollowTopicClicked,
  onUnfollowTopicClicked,
  filters,
  children,
}) => {
  return (
    <DashboardFilterProvider
      filters={{
        dateRange: {
          type: 'dateRange',
          config: {
            interval: [new Date(Date.parse(filters.from)), new Date(Date.parse(filters.to))],
            value: 'custom',
          },
        },
        account: {
          type: 'account',
          config: {
            selected: filters.accountId || '',
          },
        },
      }}
    >
      <Wrapper
        open={open}
        topicId={topicId}
        onClose={onClose}
        onEditTopicClicked={onEditTopicClicked}
        onFollowTopicClicked={onFollowTopicClicked}
        onUnfollowTopicClicked={onUnfollowTopicClicked}
      >
        {children}
      </Wrapper>
    </DashboardFilterProvider>
  );
};
